/**
 * LA FICHE DE COURS EN DONNÉES — le contrat (MV-SAV-01, MV-SAV-02, AD-MV-14).
 *
 * Une fiche est « la feuille qu'un bon prof remet à sa classe ». Elle n'est
 * pas un composant : c'est un document JSON à vocabulaire FERMÉ, que
 * l'application rend nativement, que la console relit, et qu'un auteur —
 * humain ou agent — écrit sans toucher une ligne de code d'application.
 *
 * Ce fichier porte les TYPES, les listes fermées et quelques fonctions pures
 * (ancres, typographie, parcours). Il n'importe pas zod : l'application le lit
 * sans embarquer de validateur. Le schéma qui valide ces types est à côté,
 * `fiche-cours.schema.ts` ; l'égalité des deux est vérifiée à la compilation.
 *
 * Le vocabulaire ne s'invente pas : chaque bloc, chaque primitive de dessin
 * sort du relevé des sept fiches de la maquette
 * (`docs/pilotage/chantier-manuel-vivant/FORMAT-DES-FICHES.md` § 1).
 *
 * ─── Les trois écritures d'un texte ────────────────────────────────────────
 *
 *  - `TexteRiche` : de la prose, où les mathématiques s'écrivent en LaTeX
 *    restreint entre `\(` et `\)` — l'écriture de tout le produit.
 *  - `Latex` : UNE formule, sans délimiteurs. C'est là, et là seulement, que
 *    `\cible{nom}{…}` nomme un morceau qu'une annotation peut viser.
 *  - `TexteManuscrit` : la main du prof. Du texte nu ; `^{…}` y met en
 *    exposant (« 5^{−2}, c'est 1 sur 5^{2}. »). Pas de LaTeX : une marge
 *    n'est pas une formule.
 */
export const FORMAT_FICHE_COURS = 1;
/* -------------------------------------------------------------------------- */
/* 1. Le registre des dessins                                                 */
/* -------------------------------------------------------------------------- */
/**
 * La toile d'un dessin mesure 720 unités de large par défaut ; tout s'y place
 * en unités de toile, et le rendu met l'ensemble à l'échelle de la colonne.
 * `y` d'un texte est sa LIGNE DE BASE.
 */
export const LARGEUR_TOILE = 720;
export const ANCRAGES_TEXTE = ['debut', 'milieu', 'fin'];
/** L'encre d'une écriture mathématique. Un RÔLE, jamais une couleur. */
export const TONS_ECRIT = ['encre', 'accent', 'doux', 'ardoise'];
/** Le trait : la plume du prof, un filet discret, ou l'encre de la formule. */
export const TONS_TRAIT = ['main', 'doux', 'encre'];
/** Une surface : le papier, ce qu'on regarde, ce qui est à revoir. */
export const TONS_ZONE = ['papier', 'teinte', 'teinte_forte', 'ardoise'];
/** Un jeton : le facteur qu'on compte, un autre facteur, celui qui s'est simplifié. */
export const TONS_JETON = ['accent', 'papier', 'efface'];
export const TONS_POINT = ['accent', 'doux'];
/** Le registre, dans l'ordre où la note de décision le présente. */
export const TYPES_ELEMENT_DESSIN = [
    'ecrit',
    'main',
    'etiquette',
    'accolade',
    'fleche',
    'trait',
    'zone',
    'jetons',
    'paquets',
    'groupes',
    'arbre',
    'escalier',
    'virgule',
    'axe',
    'grille',
    'cube',
];
/** De quel côté du sens `de → a` une cote pose sa formule, tel qu'on le lit sur la figure. */
export const COTES_DE_COTE = ['gauche', 'droite'];
export const MARQUES_ANNOTATION = ['souligne', 'entoure', 'aucune'];
export const ARRIVEES_ANNOTATION = ['dessous', 'dessus', 'droite'];
export const TYPES_BLOC_FICHE = [
    'idee',
    'pourquoi',
    'exemple_annote',
    'piege',
    'a_retenir',
    'verifie_toi',
    'tableau_de_lois',
];
/* -------------------------------------------------------------------------- */
/* 3. La fiche                                                                */
/* -------------------------------------------------------------------------- */
export const NATURES_FICHE = ['notion', 'revision'];
/** L'ancre d'un piège : `MAT-3-EXP-N2-E-PROD` → `piege-mat-3-exp-n2-e-prod`. */
export function ancreDuPiege(codeErreur) {
    return `piege-${codeErreur.toLowerCase()}`;
}
/** Les intitulés du gabarit — les mêmes sur toutes les fiches. */
export const INTITULES_FICHE = {
    idee: "L'idée en une phrase",
    pourquoi: "Pourquoi c'est vrai",
    exemple: 'Un exemple annoté',
    piege: 'Le piège',
    pieges: 'Les pièges',
    aRetenir: 'À retenir',
    verifieToi: 'Vérifie-toi',
    voirLaReponse: 'Voir la réponse',
    aRevoir: 'À revoir',
    juste: 'Juste',
    demandeToi: 'Demande-toi :',
};
/** Le texte nu d'un texte riche : les délimiteurs et les commandes retirés. Pour un titre d'ancre. */
function texteNu(texte) {
    return texte.replace(/\\[()]/g, '').trim();
}
/**
 * Les ancres d'une fiche, dans l'ordre de lecture. DÉDUITES des blocs : une
 * ancre sans bloc ne peut pas exister, et le sommaire ne peut pas contredire
 * la feuille. C'est cette liste que l'import écrit dans `AncreFiche`.
 *
 * Les pièges consécutifs sont rangés sous une ancre de section, `pieges`.
 */
export function ancresDeFiche(fiche) {
    const ancres = [];
    const nombreDePieges = fiche.blocs.filter((b) => b.type === 'piege').length;
    let sectionPiegesPosee = false;
    for (const bloc of fiche.blocs) {
        switch (bloc.type) {
            case 'idee':
                ancres.push({ ancre: 'idee', titre: INTITULES_FICHE.idee });
                break;
            case 'pourquoi':
                ancres.push({ ancre: 'pourquoi', titre: bloc.titreSommaire ?? INTITULES_FICHE.pourquoi });
                break;
            case 'exemple_annote':
                ancres.push({ ancre: 'exemple', titre: bloc.titreSommaire ?? INTITULES_FICHE.exemple });
                break;
            case 'piege':
                if (!sectionPiegesPosee) {
                    sectionPiegesPosee = true;
                    ancres.push({
                        ancre: 'pieges',
                        titre: nombreDePieges > 1 ? INTITULES_FICHE.pieges : INTITULES_FICHE.piege,
                    });
                }
                ancres.push({
                    ancre: ancreDuPiege(bloc.erreur),
                    titre: texteNu(bloc.titre),
                    erreur: bloc.erreur,
                });
                break;
            case 'a_retenir':
                ancres.push({ ancre: 'a-retenir', titre: bloc.intitule ?? INTITULES_FICHE.aRetenir });
                break;
            case 'verifie_toi':
                ancres.push({ ancre: 'verifie-toi', titre: INTITULES_FICHE.verifieToi });
                break;
            case 'tableau_de_lois':
                ancres.push({ ancre: 'lois', titre: bloc.titreSommaire ?? bloc.intitule });
                for (const ligne of bloc.lignes)
                    ancres.push({ ancre: ligne.ancre, titre: ligne.nom });
                break;
        }
    }
    return ancres.map((a, rang) => ({ ...a, rang }));
}
/** L'idée en une phrase d'une fiche : son aperçu dans une liste. */
export function ideeDeFiche(fiche) {
    const bloc = fiche.blocs.find((b) => b.type === 'idee');
    return bloc?.phrase ?? '';
}
/* -------------------------------------------------------------------------- */
/* 5. La typographie française                                                */
/* -------------------------------------------------------------------------- */
/**
 * Espace insécable avant « : ; ? ! » et à l'intérieur des guillemets. L'auteur
 * écrit des espaces ordinaires ; elle se pose au rendu — une ponctuation
 * orpheline en début de ligne se voit.
 */
export function typographier(texte) {
    return texte.replace(/ ([:;?!»])/g, ' $1').replace(/« /g, '« ');
}
/**
 * Un intitulé en capitales (« À RETENIR », l'en-tête d'une colonne), SANS
 * toucher aux formules : `\(x\)` reste `\(x\)`. Mettre en capitales la source
 * entière en ferait `\(X\)` — une autre variable — ou `\FRAC`, une commande
 * inconnue que le rendu montrerait brute.
 */
export function majusculesHorsFormules(texte) {
    return texte
        .split(/(\\\(.*?\\\))/s)
        .map((morceau, i) => (i % 2 === 1 ? morceau : morceau.toUpperCase()))
        .join('');
}
function ecrituresDeDessin(ill, chemin, sortie) {
    sortie.push({ chemin: `${chemin}.decrit`, sorte: 'manuscrit', texte: ill.decrit });
    if (ill.genre === 'diagramme' || ill.genre === 'solide')
        return;
    if (ill.genre === 'table') {
        ill.lignes.forEach((l, i) => {
            sortie.push({ chemin: `${chemin}.lignes[${i}].nom`, sorte: 'riche', texte: l.nom });
            l.valeurs.forEach((v, j) => {
                sortie.push({ chemin: `${chemin}.lignes[${i}].valeurs[${j}]`, sorte: 'latex', texte: v });
            });
        });
        return;
    }
    if (ill.genre === 'figure_v2') {
        ill.annotations?.forEach((a, i) => {
            if (a.type !== 'fleche') {
                sortie.push({ chemin: `${chemin}.annotations[${i}].tex`, sorte: 'latex', texte: a.tex });
            }
        });
        return;
    }
    if (ill.genre !== 'dessin')
        return;
    ill.elements.forEach((e, i) => {
        const ici = `${chemin}.elements[${i}]`;
        const latex = (cle, texte) => {
            sortie.push({ chemin: `${ici}.${cle}`, sorte: 'latex', texte });
        };
        const nu = (cle, texte) => {
            sortie.push({ chemin: `${ici}.${cle}`, sorte: 'manuscrit', texte });
        };
        switch (e.type) {
            case 'ecrit':
                latex('tex', e.tex);
                break;
            case 'main':
            case 'etiquette':
                nu('texte', e.texte);
                break;
            case 'jetons':
                nu('texte', e.texte);
                break;
            case 'paquets':
                e.contenu.forEach((j, k) => nu(`contenu[${k}].texte`, j.texte));
                break;
            case 'groupes':
                if (e.signe !== undefined)
                    latex('signe', e.signe);
                break;
            case 'arbre':
                if (e.etiquette !== undefined)
                    nu('etiquette', e.etiquette);
                break;
            case 'escalier':
                e.marches.forEach((m, k) => {
                    latex(`marches[${k}].haut`, m.haut);
                    latex(`marches[${k}].bas`, m.bas);
                });
                if (e.saut !== undefined)
                    nu('saut', e.saut);
                break;
            case 'virgule':
                nu('chiffres', e.chiffres);
                break;
            case 'axe':
                e.reperes.forEach((r, k) => {
                    nu(`reperes[${k}].nom`, r.nom);
                    if (r.tex !== undefined)
                        latex(`reperes[${k}].tex`, r.tex);
                });
                break;
            case 'accolade':
            case 'fleche':
            case 'trait':
            case 'zone':
            case 'grille':
            case 'cube':
                break;
        }
    });
}
/**
 * Toutes les chaînes qu'une fiche donne à lire, avec leur chemin et leur
 * écriture. C'est ce que le validateur passe au diagnostic LaTeX, ce que la
 * console liste pour la relecture, et ce que le test de conversion compare à
 * la maquette.
 */
export function ecrituresDeFiche(fiche) {
    const sortie = [];
    const riche = (chemin, texte) => {
        sortie.push({ chemin, sorte: 'riche', texte });
    };
    const latex = (chemin, texte) => {
        sortie.push({ chemin, sorte: 'latex', texte });
    };
    riche('titre', fiche.titre);
    fiche.blocs.forEach((bloc, i) => {
        const b = `blocs[${i}]`;
        switch (bloc.type) {
            case 'idee':
                riche(`${b}.phrase`, bloc.phrase);
                if (bloc.appui !== undefined)
                    latex(`${b}.appui.formule`, bloc.appui.formule);
                break;
            case 'pourquoi':
                bloc.figures.forEach((f, k) => {
                    ecrituresDeDessin(f.illustration, `${b}.figures[${k}].illustration`, sortie);
                    if (f.legende?.formule !== undefined) {
                        latex(`${b}.figures[${k}].legende.formule`, f.legende.formule);
                    }
                    if (f.legende !== undefined)
                        riche(`${b}.figures[${k}].legende.texte`, f.legende.texte);
                });
                break;
            case 'exemple_annote':
                riche(`${b}.enonce`, bloc.enonce);
                bloc.lignes.forEach((ligne, k) => {
                    latex(`${b}.lignes[${k}].tex`, ligne.tex);
                    ligne.notes?.forEach((n, m) => {
                        sortie.push({
                            chemin: `${b}.lignes[${k}].notes[${m}].texte`,
                            sorte: 'manuscrit',
                            texte: n.texte,
                        });
                    });
                });
                if (bloc.conclusion !== undefined)
                    riche(`${b}.conclusion`, bloc.conclusion);
                if (bloc.figure !== undefined)
                    ecrituresDeDessin(bloc.figure, `${b}.figure`, sortie);
                break;
            case 'piege':
                riche(`${b}.titre`, bloc.titre);
                riche(`${b}.question`, bloc.question);
                latex(`${b}.faux`, bloc.faux);
                riche(`${b}.tentant`, bloc.tentant);
                latex(`${b}.juste`, bloc.juste);
                riche(`${b}.parce`, bloc.parce);
                if (bloc.figure !== undefined)
                    ecrituresDeDessin(bloc.figure, `${b}.figure`, sortie);
                break;
            case 'a_retenir':
                bloc.lignes.forEach((ligne, k) => riche(`${b}.lignes[${k}]`, ligne));
                break;
            case 'verifie_toi':
                riche(`${b}.question`, bloc.question);
                bloc.items?.forEach((item, k) => riche(`${b}.items[${k}]`, item));
                bloc.reponse.forEach((p, k) => riche(`${b}.reponse[${k}]`, p));
                if (bloc.figure !== undefined)
                    ecrituresDeDessin(bloc.figure, `${b}.figure`, sortie);
                break;
            case 'tableau_de_lois':
                bloc.lignes.forEach((ligne, k) => {
                    ligne.regle.forEach((r, m) => latex(`${b}.lignes[${k}].regle[${m}]`, r));
                    latex(`${b}.lignes[${k}].exemple`, ligne.exemple);
                    riche(`${b}.lignes[${k}].piege`, ligne.piege);
                });
                if (bloc.conditions !== undefined)
                    riche(`${b}.conditions`, bloc.conditions);
                break;
        }
    });
    return sortie;
}
//# sourceMappingURL=fiche-cours.js.map